import React, { useEffect, useMemo, useRef } from 'react';
import { Outlet, useLocation, useNavigate, createSearchParams } from 'react-router-dom';
import { useFilmContext } from '../../contexts/FilmContext';
import { useTranslation } from 'react-i18next';
import { useDataCache } from '../../contexts/BreakdownDataCacheContext';
import { BudgetDataCacheProvider, useBudgetDataCache } from '../../contexts/BudgetDataCacheContext';
import { TabCacheProvider, useTabCache, extractBusinessIdFromTab } from '../../contexts/TabCacheContext';
import {
  BreakdownTabProvider,
  useBreakdownTabContext,
  type BreakdownTabType,
} from '../../contexts/BreakdownTabContext';
import { BudgetTabProvider, useBudgetTabContext } from '../../contexts/BudgetTabContext';
import { useCurrentUser } from '../../contexts/CurrentUserContext';
import { getSchedulesByBreakdownId } from '../../api/schedule';
import { resolveEffectiveScheduleId } from '../../utils/resolveUserLastUsed';
import TabBar from '../TabBar/TabBar';
import WindowControls from '../WindowControls';
import './Layout.css';

// 检查是否在Electron环境中
const isElectron = typeof window !== 'undefined' && window.require && window.require('electron');

interface Tab {
  id: string;
  title: string;
  active: boolean;
  fixed?: boolean;
  path?: string;
  previousTabId?: string; // 记录进入这个标签之前的标签ID
}

// Layout 内部组件，使用 TabCache
const LayoutContent: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedFilmID, reloadCurrentFilmChildren } = useFilmContext();
  /** 挂在 Layout 上，避免 Home 卸载时丢失「上一路径」，否则从 /breakdown 回 / 时无法触发刷新 */
  const prevPathnameForFilmChildrenRef = useRef<string | null>(null);
  const { clearCache: clearTabCache, getTabIdByBusinessId } = useTabCache();

  useEffect(() => {
    const prev = prevPathnameForFilmChildrenRef.current;
    prevPathnameForFilmChildrenRef.current = location.pathname;

    if (location.pathname !== '/') return;
    if (!selectedFilmID) return;
    if (prev !== null && prev !== '/') {
      void reloadCurrentFilmChildren();
    }
  }, [location.pathname, selectedFilmID, reloadCurrentFilmChildren]);
  
  // 保存主页的路径状态
  const [homeState, setHomeState] = React.useState('/');
  // 当前选中的分场ID
  const [currentBreakdownId, setCurrentBreakdownId] = React.useState<string | null>(null);
  const [tabs, setTabs] = React.useState<Tab[]>(() => {
    const savedTabs = localStorage.getItem('tabs');
    return savedTabs ? JSON.parse(savedTabs) : [
      { id: 'home', title: t('common.home'), active: true, fixed: true, path: '/' }
    ];
  });

  // 从路径中提取 breakdownId（/breakdown/:id、/elements/:id、旧 /element-manager/:id）
  useEffect(() => {
    const breakdownMatch = location.pathname.match(/\/breakdown\/([^/]+)/);
    const elementsMatch = location.pathname.match(/\/elements\/([^/]+)/);
    const elementManagerMatch = location.pathname.match(/\/element-manager\/([^/]+)/);

    if (breakdownMatch) {
      setCurrentBreakdownId(breakdownMatch[1]);
    } else if (elementsMatch) {
      setCurrentBreakdownId(elementsMatch[1]);
    } else if (elementManagerMatch) {
      setCurrentBreakdownId(elementManagerMatch[1]);
    } else if (location.pathname === '/') {
      setCurrentBreakdownId(null);
    } else {
      // 对于其他路径（如 /schedule/:scheduleID、/budgets），保持当前的 breakdownId 不变
    }
  }, [location.pathname]);

  // 设置主应用窗口大小
  useEffect(() => {
    if (isElectron) {
      try {
        const { ipcRenderer } = window.require('electron');
        // 发送消息到主进程来调整窗口大小为主应用大小
        ipcRenderer.send('resize-window', { width: 1800, height: 1000 });
      } catch (error) {
        console.log('Failed to resize window:', error);
      }
    }
  }, []); // 只在组件挂载时执行一次


  // 监听语言变化，更新标签标题
  useEffect(() => {
    setTabs(prevTabs => 
      prevTabs.map(tab => {
        // 根据标签ID更新对应的标题
        if (tab.id === 'home') {
          return { ...tab, title: t('common.home') };
        }
        // 其他标签保持原标题（通常是动态内容如影片名称）
        return tab;
      })
    );
  }, [i18n.language, t]);

  const user = {
    name: '张三',
    avatar: 'https://via.placeholder.com/36'
  };

  // 优化：只在标签结构发生变化时保存到localStorage
  useEffect(() => {
    localStorage.setItem('tabs', JSON.stringify(tabs));
  }, [tabs]);

  // 优化：只监听激活标签的变化
  const activeTabId = useMemo(() => tabs.find(tab => tab.active)?.id, [tabs]);
  
  // 保存主页状态 - 只在激活标签变化时触发
  useEffect(() => {
    if (activeTabId === 'home' && location.pathname !== '/') {
      setHomeState(location.pathname);
    }
  }, [location.pathname, activeTabId]);

  // 优化：路由同步逻辑 - 减少依赖，避免循环更新
  useEffect(() => {
    const currentPath = location.pathname;
    
    setTabs(prevTabs => {
      const activeTab = prevTabs.find(tab => tab.active);
      const matchingTab = prevTabs.find(tab => tab.path === currentPath);
      
      // 只在路径变化且没有匹配的激活标签时才更新
      if (!activeTab || (activeTab.path !== currentPath)) {
        if (matchingTab && !matchingTab.active) {
          // 如果找到匹配的标签，激活它
          return prevTabs.map(tab => ({
            ...tab,
            active: tab.id === matchingTab.id
          }));
        } else if (!matchingTab && currentPath !== '/') {
          // 如果没有匹配的标签且当前路径不是主页，则取消所有标签的激活状态（包括主页标签）
          return prevTabs.map(tab => ({
            ...tab,
            active: false
          }));
        } else if (currentPath === '/' && activeTab && activeTab.id !== 'home') {
          // 如果当前路径是主页，但激活的不是主页标签，则激活主页标签
          return prevTabs.map(tab => ({
            ...tab,
            active: tab.id === 'home'
          }));
        }
      }
      
      // 如果没有变化，返回原状态
      return prevTabs;
    });
  }, [location.pathname, setTabs]); // 只依赖路径，使用函数式更新避免依赖 tabs

  // 注意：这里不能直接使用 useBreakdownTabContext，因为 LayoutContent 还没有被 BreakdownTabProvider 包裹
  // 实际的标签切换逻辑在 LayoutContentInner 中处理
  const handleTabClick = (id: string, getBreakdownCache?: (breakdownId: string) => any) => {
    // 固定标签的点击处理将在 LayoutContentInner 中通过 Context 处理
    // 这里先保留原有逻辑作为后备

    const targetTab = tabs.find(tab => tab.id === id);
    if (!targetTab) return;

    const currentTab = tabs.find(tab => tab.active);
    
    // 如果点击主页标签，导航到主页
    if (id === 'home') {
      setTabs(tabs.map(tab => ({
        ...tab,
        active: tab.id === id
      })));
      navigate('/');
      return;
    }

    // 记录标签切换历史：如果切换到不同的标签，记录当前标签为目标标签的前一个标签
    if (currentTab && currentTab.id !== id) {
      setTabs(tabs.map(tab => ({
        ...tab,
        active: tab.id === id,
        previousTabId: tab.id === id ? currentTab.id : tab.previousTabId
      })));
    } else {
      setTabs(tabs.map(tab => ({
        ...tab,
        active: tab.id === id
      })));
    }

    // Navigate to the tab's path
    if (targetTab.path) {
      if (targetTab.id.startsWith('create-budget-')) {
        const budgetID = targetTab.id.replace('create-budget-', '');
        navigate(targetTab.path, {
          state: {
            budgetID,
            budgetName: targetTab.title,
            tabId: targetTab.id
          }
        });
      } else {
        navigate(targetTab.path);
      }
    }
  };

  const handleTabClose = (id: string) => {
    if (tabs.find(tab => tab.id === id)?.fixed) return;
    
    const currentTab = tabs.find(tab => tab.id === id);
    const currentIndex = tabs.findIndex(tab => tab.id === id);
    
    // 清除该标签页的 TabCache
    if (currentTab) {
      clearTabCache(id);
    }
    
    // If closing active tab, decide which tab to activate next
    if (currentTab?.active) {
      let nextActiveTab = null;
      
      // 1. 首先尝试跳转到进入当前标签之前的标签（如果还存在）
      if (currentTab.previousTabId) {
        nextActiveTab = tabs.find(tab => tab.id === currentTab.previousTabId);
      }
      
      // 2. 如果前一个标签不存在，尝试跳转到左边的标签
      if (!nextActiveTab && currentIndex > 0) {
        nextActiveTab = tabs[currentIndex - 1];
      }
      
      // 3. 如果左边没有标签，尝试跳转到右边的标签
      if (!nextActiveTab && currentIndex < tabs.length - 1) {
        nextActiveTab = tabs[currentIndex + 1];
      }
      
      // 4. 如果没有其他标签了，跳转到主页标签
      if (!nextActiveTab) {
        nextActiveTab = tabs.find(tab => tab.id === 'home');
      }
      
      if (nextActiveTab) {
        handleTabClick(nextActiveTab.id);
      }
    }
    
    setTabs(prevTabs => prevTabs.filter(tab => tab.id !== id));
  };


  // 优化：使用稳定的context对象，避免因仅标签名称变化导致的重新渲染
  const contextValue = React.useMemo(() => ({ 
    tabs, 
    setTabs,
    // 提供检查相同业务ID标签是否已存在的方法
    findTabByBusinessId: (businessId: string) => {
      const existingTabId = getTabIdByBusinessId(businessId);
      return existingTabId ? tabs.find(tab => tab.id === existingTabId) : null;
    }
  }), [tabs, setTabs, getTabIdByBusinessId]);

  return (
    <BudgetDataCacheProvider>
      <BreakdownTabProvider>
        <BudgetTabProvider>
          <LayoutContentInner 
              tabs={tabs}
              setTabs={setTabs}
              homeState={homeState}
              setHomeState={setHomeState}
              handleTabClose={handleTabClose}
              handleTabClickBase={handleTabClick}
              contextValue={contextValue}
              user={user}
              currentBreakdownId={currentBreakdownId}
              isHomePage={location.pathname === '/'}
            />
          </BudgetTabProvider>
        </BreakdownTabProvider>
      </BudgetDataCacheProvider>
  );
};

// 内部组件：在 Provider 内部使用，可以访问所有缓存上下文
const LayoutContentInner: React.FC<{
  tabs: Tab[];
  setTabs: React.Dispatch<React.SetStateAction<Tab[]>>;
  homeState: string;
  setHomeState: React.Dispatch<React.SetStateAction<string>>;
  handleTabClose: (id: string) => void;
  handleTabClickBase: (id: string, getBreakdownCache?: (breakdownId: string) => any) => void;
  contextValue: any;
  user: any;
  currentBreakdownId: string | null;
  isHomePage: boolean;
}> = ({ tabs, handleTabClose, handleTabClickBase, contextValue, user, currentBreakdownId, isHomePage }) => {
  const { clearCache: clearBudgetCache, clearAllCache: clearAllBudgetDataCache } = useBudgetDataCache();
  const {
    clearSceneCache,
    clearBreakdownCache,
    getBreakdownCache,
    clearAllCache: clearAllBreakdownDataCache,
    getScheduleCache,
  } = useDataCache();
  const { breakdownId: contextBreakdownId, switchTab } = useBreakdownTabContext();
  const { budgetId, switchTab: switchBudgetTab } = useBudgetTabContext();
  const { setLocalOnlyField, serverSyncedData } = useCurrentUser();
  const navigate = useNavigate();
  const location = useLocation();
  
  // 包装 handleTabClick，处理固定标签的点击
  const handleTabClick = (id: string) => {
    // 回主页：清空分场与预算业务缓存，再导航
    if (id === 'home') {
      clearAllBreakdownDataCache();
      clearAllBudgetDataCache();
      handleTabClickBase(id, getBreakdownCache);
      return;
    }
    
    // 处理固定标签的点击 - 不再导航，而是切换标签
    // 检查是否有 breakdownId（从 Context 或 props）
    const hasBreakdownId = contextBreakdownId || currentBreakdownId;
    
    // 分场分景固定标签的处理
    if (id === 'breakdown-tab' || id === 'schedule-tab' || id === 'element-manager-tab') {
      if (!hasBreakdownId) {
        console.warn('无法切换标签：没有 breakdownId', { contextBreakdownId, currentBreakdownId });
        return;
      }
      
      // 根据标签ID映射到对应的 tab 类型
      let tabType: BreakdownTabType;
      let tabIdentifier: string;
      if (id === 'breakdown-tab') {
        tabType = 'breakdown';
        tabIdentifier = 'breakdown-tab';
      } else if (id === 'schedule-tab') {
        tabType = 'schedule';
        tabIdentifier = 'schedule-tab';
      } else {
        tabType = 'element-manager';
        tabIdentifier = 'element-manager-tab';
      }

      const breakdownIdToUse = contextBreakdownId || currentBreakdownId;
      if (!breakdownIdToUse) {
        return;
      }

      // 更新用户行为记忆缓存：记录最后使用的标签页
      const cacheValue = `${breakdownIdToUse};${tabIdentifier}`;
      setLocalOnlyField('userLastUsedBreakdownTab', cacheValue);

      // 先切换 UI 标签，再导航到与标签一致的 URL
      switchTab(tabType);

      if (id === 'breakdown-tab') {
        navigate(`/breakdown/${breakdownIdToUse}`, { replace: true });
        return;
      }

      if (id === 'element-manager-tab') {
        const currentScheduleId = getScheduleCache(breakdownIdToUse)?.currentScheduleId;
        navigate(
          currentScheduleId
            ? `/elements/${breakdownIdToUse}?schedule=${encodeURIComponent(currentScheduleId)}`
            : `/elements/${breakdownIdToUse}`,
          { replace: true },
        );
        return;
      }

      // schedule-tab：需要可用的 scheduleID
      const bid = breakdownIdToUse;
      const slice = getScheduleCache(bid);
      let sid = slice?.currentScheduleId ?? null;
      if (sid && slice?.schedules?.length && !slice.schedules.some((p) => p.scheduleID === sid)) {
        sid = null;
      }
      if (!sid && slice?.schedules?.length) {
        sid = resolveEffectiveScheduleId(
          serverSyncedData.userLastUsedScheduleID,
          bid,
          slice.schedules,
        );
      }
      if (sid) {
        navigate(`/schedule/${sid}`, { replace: true });
        return;
      }
      void (async () => {
        try {
          const res = await getSchedulesByBreakdownId(bid);
          const list = res.data || [];
          const resolved = resolveEffectiveScheduleId(
            serverSyncedData.userLastUsedScheduleID,
            bid,
            list,
          );
          if (resolved) {
            navigate(`/schedule/${resolved}`, { replace: true });
          } else {
            navigate(
              {
                pathname: '/schedule',
                search: createSearchParams({ tab: 'details' }).toString(),
              },
              {
                replace: true,
                state: { noScheduleSelected: true, breakdownID: bid },
              },
            );
          }
        } catch {
          navigate(`/breakdown/${bid}`, { replace: true });
        }
      })();
      return;
    }
    
    // 预算固定标签的处理
    if (id === 'budget-tab' || id === 'financial-tab' || id === 'analysis-tab') {
      if (!budgetId) {
        console.warn('无法切换标签：没有 budgetId', { budgetId });
        return;
      }
      
      // 根据标签ID映射到对应的 tab 类型
      let tabType: 'budget' | 'financial' | 'analysis';
      if (id === 'budget-tab') {
        tabType = 'budget';
      } else if (id === 'financial-tab') {
        tabType = 'financial';
      } else {
        tabType = 'analysis';
      }
      
      console.log('切换预算标签:', { id, tabType, budgetId, currentPath: location.pathname });
      
      // 先切换标签
      switchBudgetTab(tabType);
      
      // 确保 URL 是 /budgets（如果当前不在 /budgets 页面，先导航）
      if (location.pathname !== '/budgets') {
        console.log('导航到 /budgets');
        navigate('/budgets', { replace: true });
      }
      return;
    }
    
    // 其他标签的处理
    handleTabClickBase(id, getBreakdownCache);
  };
  
  // 包装 handleTabClose，添加业务缓存清除逻辑
  const handleTabCloseWithCacheClear = (id: string) => {
    const currentTab = tabs.find(tab => tab.id === id);
    
    // 先清除业务缓存
    if (currentTab) {
      const businessId = extractBusinessIdFromTab(id, currentTab.path || '');
      if (businessId) {
        // Budget 标签页
        if (id.startsWith('budget-') || id.startsWith('create-budget-') || (currentTab.path && currentTab.path.startsWith('/budget/'))) {
          clearBudgetCache(businessId);
        }
        // Scene 标签页
        else if (id.startsWith('scene-') || (currentTab.path && currentTab.path.startsWith('/scene/'))) {
          clearSceneCache(businessId);
        }
        // Breakdown 标签页
        else if (
          id.startsWith('breakdownList-') ||
          (currentTab.path &&
            (currentTab.path.startsWith('/breakdown/') || currentTab.path.startsWith('/elements/')))
        ) {
          clearBreakdownCache(businessId);
        }
      }
    }
    
    // 然后调用原来的 handleTabClose（清除 TabCache）
    handleTabClose(id);
  };

  return (
    <div className="layout">
      <WindowControls />
      <div className="layout-main">
        <TabBar
          tabs={tabs}
          user={user}
          onTabClick={handleTabClick}
          onTabClose={handleTabCloseWithCacheClear}
          currentBreakdownId={currentBreakdownId}
          isHomePage={isHomePage}
          currentPath={location.pathname}
        />
        <div className="layout-content">
          {/* 优化：使用稳定的context对象，减少子组件重新渲染 */}
          <Outlet context={contextValue} />
        </div>
      </div>
    </div>
  );
};

// Layout 主组件，提供 TabCacheProvider
const Layout: React.FC = () => {
  return (
    <TabCacheProvider>
      <LayoutContent />
    </TabCacheProvider>
  );
};

export default Layout;
